/**
 * AG-UI Data Standardizer Component
 * Automatically detects and standardizes data visualization based on AG-UI protocol
 * Handles diverse data types dynamically without hardcoded responses
 */

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  Chip
} from '@mui/material';
import {
  TableChart as TableIcon,
  BarChart as ChartIcon,
  Code as JsonIcon,
  TextFields as TextIcon,
  DataObject as DataIcon
} from '@mui/icons-material';

// Simple table and chart components for AG-UI data visualization
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';

// Import Recharts for data visualization
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Helper function to extract raw data from agent state
const extractRawData = (agentState) => {
  // First check if we have structured data directly available
  if (agentState.data && Array.isArray(agentState.data)) {
    return agentState.data;
  }

  const toolResults = agentState.toolResults || [];
  
  // Extract from tool results - check for structured data first
  for (const tool of toolResults) {
    // Check for structured data in tool.data
    if (tool.data && Array.isArray(tool.data)) {
      return tool.data;
    }
    
    const result = tool.result;
    
    // Handle direct string result (new backend format)
    if (typeof result === 'string' && result.trim()) {
      return result;
    }
    
    // Handle nested content structure (legacy format)
    if (result?.content && Array.isArray(result.content)) {
      const textContent = result.content.find(item => 
        item.text?.trim() && item.text !== '\r\n'
      );
      if (textContent) {
        return textContent.text;
      }
    }
    
    // Handle content as string
    if (typeof result?.content === 'string' && result.content.trim()) {
      return result.content;
    }
  }
  
  // Check structured rawData
  if (agentState.rawData) {
    if (Array.isArray(agentState.rawData) && agentState.rawData.length > 0) {
      // If it's an array of objects (structured data), return it
      if (typeof agentState.rawData[0] === 'object' && !agentState.rawData[0].text) {
        return agentState.rawData;
      }
      // If it's an array with text objects, extract the text
      for (const item of agentState.rawData) {
        if (typeof item.text === 'string' && item.text.trim()) {
          return item.text;
        }
      }
    } else if (typeof agentState.rawData === 'string') {
      return agentState.rawData;
    }
    // --- Robust: If rawData is an error object with a raw string, try to parse it as CSV/markdown ---
    if (
      typeof agentState.rawData === 'object' &&
      agentState.rawData.error &&
      typeof agentState.rawData.raw === 'string'
    ) {
      // Try CSV first
      const csvParsed = parseCSVData(agentState.rawData.raw);
      if (csvParsed) return csvParsed;
      // Try markdown table
      const mdParsed = parseMarkdownTable(agentState.rawData.raw);
      if (mdParsed) return mdParsed;
      // Fallback: return the raw string for display
      return agentState.rawData.raw;
    }
  }
  
  return agentState.rawData || null;
};

// Helper function to analyze data type and structure
const analyzeDataStructure = (rawData) => {
  let dataType = 'unknown';
  let availableViews = ['text'];
  let processedData = rawData;
  let descriptiveText = '';
  let tableData = null;

  if (typeof rawData === 'string') {
    // Check for markdown table format
    if (rawData.includes('|') && rawData.includes('\n')) {
      const lines = rawData.split('\n');
      const tableStartIndex = lines.findIndex(line => line.includes('|') && line.includes('-'));
      
      if (tableStartIndex > 0) {
        // Extract descriptive text before the table
        descriptiveText = lines.slice(0, tableStartIndex - 1).join('\n').trim();
        
        // Extract table lines (header + separator + data)
        const tableLines = lines.slice(tableStartIndex - 1).filter(line => line.includes('|'));
        
        if (tableLines.length >= 3) { // header + separator + at least one data row
          tableData = parseMarkdownTable(tableLines.join('\n'));
          
          if (tableData && tableData.length > 0) {
            dataType = 'tabular';
            processedData = {
              description: descriptiveText,
              data: tableData,
              originalText: rawData
            };
            availableViews = ['chart', 'table', 'text'];
            return { dataType, processedData, availableViews };
          }
        }
      }
      
      // Fallback: try to parse the entire string as a table
      const csvData = parseMarkdownTable(rawData);
      if (csvData && csvData.length > 0) {
        dataType = 'tabular';
        processedData = {
          description: '',
          data: csvData,
          originalText: rawData
        };
        availableViews = ['chart', 'table', 'text'];
        return { dataType, processedData, availableViews };
      }
    }

    // Check for CSV format
    if (rawData.includes(',') && rawData.includes('\n')) {
      const csvData = parseCSVData(rawData);
      if (csvData) {
        dataType = 'tabular';
        processedData = {
          description: '',
          data: csvData,
          originalText: rawData
        };
        availableViews = ['chart', 'table', 'text'];
        return { dataType, processedData, availableViews };
      }
    }

    // Check for JSON format
    try {
      const jsonData = JSON.parse(rawData);
      if (Array.isArray(jsonData) && jsonData.length > 0) {
        dataType = 'array';
        processedData = jsonData;
        availableViews = ['table', 'json', 'text'];
        if (typeof jsonData[0] === 'object') {
          availableViews.push('chart');
        }
      } else if (typeof jsonData === 'object' && jsonData !== null) {
        // Support structured object { description, data: [...] } for AG-UI
        if (jsonData.description !== undefined && Array.isArray(jsonData.data)) {
          dataType = 'tabular';
          processedData = {
            description: jsonData.description || '',
            data: jsonData.data,
            originalText: rawData
          };
          availableViews = ['chart', 'table', 'json', 'text'];
        } else {
          dataType = 'object';
          processedData = jsonData;
          availableViews = ['json', 'text'];
        }
      }
    } catch (e) {
      // Not JSON, keep as text
      dataType = 'text';
      availableViews = ['text'];
    }
  } else if (Array.isArray(rawData)) {
    dataType = 'array';
    availableViews = ['table', 'json', 'text'];
    if (rawData.length > 0 && typeof rawData[0] === 'object') {
      availableViews.push('chart');
    }
  } else if (typeof rawData === 'object' && rawData !== null) {
    // Support structured object { description, data: [...] } coming directly from backend
    if (rawData.description !== undefined && Array.isArray(rawData.data)) {
      dataType = 'tabular';
      processedData = {
        description: rawData.description || '',
        data: rawData.data,
        originalText: ''
      };
      availableViews = ['chart', 'table', 'json', 'text'];
    } else {
      dataType = 'object';
      processedData = rawData;
      availableViews = ['json', 'text'];
    }
  }

  return { dataType, processedData, availableViews };
};

// Helper function to parse CSV data
const parseCSVData = (rawData) => {
  try {
    const lines = rawData.split('\n').filter(line => line.trim());
    
    if (lines.length <= 1) {
      return null;
    }
    
    const headers = lines[0].split(',').map(h => h.trim());
    
    const rows = lines.slice(1).map(line => {
      const values = line.split(',');
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index]?.trim() || '';
      });
      return obj;
    });

    return rows.length > 0 ? rows : null;
  } catch (e) {
    console.warn('Failed to parse CSV data:', e);
    return null;
  }
};

// Helper function to parse markdown table
const parseMarkdownTable = (rawData) => {
  try {
    const lines = rawData.split('\n').filter(line => line.trim());
    const headerRow = lines[0];
    const separatorRow = lines[1];
    const dataRows = lines.slice(2);

    if (!headerRow || !separatorRow || dataRows.length === 0) {
      return null;
    }

    const header = headerRow.split('|').map(h => h.trim()).filter(h => h);
    const separator = separatorRow.split('|').map(s => s.trim()).filter(s => s);

    if (header.length !== separator.length) {
      return null;
    }

    const parsedData = [];
    for (const row of dataRows) {
      const values = row.split('|').map(v => v.trim()).filter(v => v);
      if (values.length === header.length) {
        const obj = {};
        for (let i = 0; i < header.length; i++) {
          obj[header[i]] = values[i];
        }
        parsedData.push(obj);
      }
    }

    return parsedData.length > 0 ? parsedData : null;
  } catch (e) {
    console.warn('Failed to parse markdown table:', e);
    return null;
  }
};

// Simple Table Component
const SimpleTable = ({ data, title }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const columns = Object.keys(data[0] || {});
  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper variant="outlined" sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        {title}
      </Typography>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column} sx={{ fontWeight: 'bold' }}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={index} hover>
                {columns.map((column) => (
                  <TableCell key={column}>
                    {String(row[column] || '')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Paper>
  );
};

// Smart Chart Component using Recharts
const SimpleChart = ({ data, title }) => {
  // Analyze data structure to determine best chart type and fields
  const sampleRow = data[0] || {};
  const fields = Object.keys(sampleRow);

  // Find potential X-axis field (time, date, or first field)
  let xField = fields.find(field =>
    field.toLowerCase().includes('time') ||
    field.toLowerCase().includes('date') ||
    field.toLowerCase().includes('timestamp')
  ) || fields[0];

  // Find numeric fields for Y-axis
  const numericFields = fields.filter(field => {
    if (field === xField) return false;
    // Check if most values in this field are numeric
    const numericCount = data.slice(0, 10).reduce((count, row) => {
      const value = row[field];
      return count + (value !== null && value !== undefined && !isNaN(Number(value)) ? 1 : 0);
    }, 0);
    return numericCount >= Math.min(1, data.length * 0.5); // At least half should be numeric
  });

  // Use available fields for charting, even if not all are numeric
  const fieldsToChart = numericFields.length > 0 ? numericFields : fields.slice(1, 3);

  // Prepare data for charting
  const chartData = data.map((row, index) => {
    const chartRow = { index };

    // Add X-axis value
    let xValue = row[xField];
    if (xValue && typeof xValue === 'string') {
      // Try to parse as date/time
      const dateValue = new Date(xValue);
      if (!isNaN(dateValue.getTime())) {
        xValue = dateValue.toLocaleTimeString();
      }
    }
    chartRow[xField] = xValue || index;

    // Add chart fields
    fieldsToChart.forEach(field => {
      const value = row[field];
      chartRow[field] = value !== null && value !== undefined && !isNaN(Number(value))
        ? Number(value)
        : null;
    });

    return chartRow;
  });

  // Determine chart type based on data characteristics
  const isTimeSeries = xField.toLowerCase().includes('time') ||
                      xField.toLowerCase().includes('date');

  // Color palette matching your app's design system
  const colors = ['#6B46C1', '#319795', '#9F7AEA', '#4FD1C5', '#553C9A', '#2C7A7B'];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Box sx={{ width: '100%', height: 400 }}>
        <ResponsiveContainer>
          {isTimeSeries ? (
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={xField}
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                labelStyle={{ color: '#333' }}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
              />
              <Legend />
              {numericFields.slice(0, 3).map((field, index) => (
                <Area
                  key={field}
                  type="monotone"
                  dataKey={field}
                  stroke={colors[index]}
                  fill={colors[index]}
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={xField}
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                labelStyle={{ color: '#333' }}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
              />
              <Legend />
              {fieldsToChart.slice(0, 3).map((field, index) => (
                <Line
                  key={field}
                  type="monotone"
                  dataKey={field}
                  stroke={colors[index]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </Box>

      {/* Chart Info */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={`X-axis: ${xField}`}
          size="small"
          variant="outlined"
        />
        {fieldsToChart.slice(0, 3).map((field, index) => (
          <Chip
            key={field}
            label={field}
            size="small"
            sx={{
              bgcolor: colors[index],
              color: 'white',
              '& .MuiChip-label': { color: 'white' }
            }}
          />
        ))}
        {fieldsToChart.length > 3 && (
          <Chip
            label={`+${fieldsToChart.length - 3} more`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    </Paper>
  );
};

/**
 * AG-UI Data Standardizer - Automatically handles any data type
 */
const AGUIDataStandardizer = ({ agentState, onFeedback }) => {
  const dataAnalysis = useMemo(() => {
    const hasData = agentState?.dataAvailable;
    
    if (!hasData) {
      return { type: 'none', data: null, views: [] };
    }

    // Extract data from toolResults or rawData
    const rawData = extractRawData(agentState);
    
    if (!rawData) {
      return { type: 'none', data: null, views: [] };
    }

    // Analyze data structure
    const { dataType, processedData, availableViews } = analyzeDataStructure(rawData);

    const result = {
      type: dataType,
      data: processedData,
      views: availableViews,
      recordCount: Array.isArray(processedData) ? processedData.length : 
                   (processedData && processedData.data && Array.isArray(processedData.data)) ? processedData.data.length : 
                   null
    };
    
    return result;
  }, [agentState]);

  const [activeView, setActiveView] = React.useState(0);

  // Auto-select best view based on available views
  React.useEffect(() => {
    const viewPriority = ['chart', 'table', 'json', 'text'];
    const preferredView = viewPriority.find(view => dataAnalysis.views.includes(view));
    const viewIndex = preferredView ? dataAnalysis.views.indexOf(preferredView) : 0;
    setActiveView(viewIndex);
  }, [dataAnalysis]);

  if (dataAnalysis.type === 'none') {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          No structured data available from this query.
        </Typography>
      </Alert>
    );
  }

  const renderView = (viewType) => {
    const { data } = dataAnalysis;

    // Handle new structured data format with description and data
    const isStructuredData = data && typeof data === 'object' && data.description !== undefined;
    const description = isStructuredData ? data.description : '';
    const actualData = isStructuredData ? data.data : data;
    const recordCount = Array.isArray(actualData) ? actualData.length : 
                       Array.isArray(data) ? data.length : 0;

    switch (viewType) {
      case 'table':
        return (
          <Box>
            {description && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {description}
                </Typography>
              </Paper>
            )}
            <SimpleTable
              data={actualData || data}
              title={`Data Table (${recordCount} records)`}
            />
          </Box>
        );

      case 'chart':
        return (
          <Box>
            {description && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {description}
                </Typography>
              </Paper>
            )}
            <SimpleChart
              data={actualData || data}
              title={`Data Visualization (${recordCount} points)`}
            />
          </Box>
        );

      case 'json':
        return (
          <Box>
            {description && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {description}
                </Typography>
              </Paper>
            )}
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {JSON.stringify(actualData || data, null, 2)}
              </Typography>
            </Paper>
          </Box>
        );

      case 'text':
        const textData = isStructuredData ? data.originalText : 
                        (typeof data === 'string' ? data : JSON.stringify(data, null, 2));
        return (
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {textData}
            </Typography>
          </Paper>
        );

      default:
        return <Typography>Unknown view type: {viewType}</Typography>;
    }
  };

  const getViewIcon = (viewType) => {
    switch (viewType) {
      case 'table': return <TableIcon />;
      case 'chart': return <ChartIcon />;
      case 'json': return <JsonIcon />;
      case 'text': return <TextIcon />;
      default: return <DataIcon />;
    }
  };

  const getViewLabel = (viewType) => {
    switch (viewType) {
      case 'table': return 'Table';
      case 'chart': return 'Chart';
      case 'json': return 'JSON';
      case 'text': return 'Raw';
      default: return viewType;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Data Info */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          icon={<DataIcon />}
          label={`Type: ${dataAnalysis.type}`}
          size="small"
          color="primary"
          variant="outlined"
        />
        {dataAnalysis.recordCount && (
          <Chip
            label={`${dataAnalysis.recordCount} records`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        <Chip
          label={`${dataAnalysis.views.length} views`}
          size="small"
          color="info"
          variant="outlined"
        />
      </Box>

      {/* View Tabs */}
      {dataAnalysis.views.length > 1 && (
        <Tabs
          value={activeView}
          onChange={(e, newValue) => setActiveView(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {dataAnalysis.views.map((viewType, index) => (
            <Tab
              key={viewType}
              icon={getViewIcon(viewType)}
              label={getViewLabel(viewType)}
              iconPosition="start"
            />
          ))}
        </Tabs>
      )}

      {/* View Content */}
      <Box>
        {renderView(dataAnalysis.views[activeView])}
      </Box>
    </Box>
  );
};

export default AGUIDataStandardizer;
